import { GetAccount, SetLoadingStatus } from '@app/finxone-web-frontend/app/lib/actions/account.action';
import { Country } from '@app/finxone-web-frontend/app/lib/dtos/countries.dto';
import { Currency } from '@app/finxone-web-frontend/app/lib/dtos/currencies.dto';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { AccountState, AccountStateModel } from '@app/finxone-web-frontend/app/lib/state/account.state';
import {
  SavingsAccountState,
  SavingsAccountStateModel,
} from '@app/finxone-web-frontend/app/lib/state/savings-account.state';
import {
  AccountStatusType,
  AccountTypes,
  BaseWidgetProperties,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  ViewType,
  baseCssConfigStyle,
  commonContentStyleList,
  commonTextContentStyleList,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { BaseComponent } from './base.component';
import {
  GetSavingAccount,
  SetSavingLoadingStatus,
} from '@app/finxone-web-frontend/app/lib/actions/savings-account.action';

export abstract class BaseAccountsListingComponent extends BaseComponent {
  public textContentStyle: baseCssConfigStyle;
  public searchIconStyle: baseCssConfigStyle;
  public searchInputStyle: baseCssConfigStyle;
  public searchInputInactiveStyle: baseCssConfigStyle;
  public widgetConfig: BaseWidgetProperties;
  public searchInputBaseStyle: baseCssConfigStyle;
  public accountsInfo: AccountStateModel | SavingsAccountStateModel;
  public contextSwitch: boolean;
  public navigateToUrl: string;
  public currencies: Currency[];
  public countries: Country[];
  public pageNumber = 1;
  public totalPages = 1;
  public contextBasedLoadingSelectors = {
    [AccountTypes.CURRENT_ACCOUNT]: AccountState.getLoadingStatus,
    account: AccountState.getLoadingStatus,
    [AccountTypes.SAVINGS_ACCOUNT]: SavingsAccountState.getLoadingStatus,
    [AccountTypes.OPEN_EASY_ACCESS_SAVINGS_ACCOUNT]: SavingsAccountState.getLoadingStatus,
    [AccountTypes.SAVINGS_INTERNAL_TRANSFER_ACCOUNT_FORM_CONTEXT]: SavingsAccountState.getLoadingStatus,
  };

  constructor(
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    public store: Store,
    public configService: ConfigService,
  ) {
    super();
  }

  public getWidgetCssConfig() {
    this.contentStyle = this.configService.getSpecificBaseCssStyle(
      this.dataIn?.attri,
      commonContentStyleList,
    );

    this.textContentStyle = this.configService.getSpecificBaseCssStyle(
      this.dataIn?.attri,
      commonTextContentStyleList,
    );

    const { iconStyle, baseStyle, activeStyle, inactiveStyle } = this.configService.getInputFieldCssStyles(
      this.widgetConfig,
    );

    this.searchIconStyle = iconStyle;
    this.searchInputStyle = baseStyle;
    this.searchInputBaseStyle = activeStyle;
    this.searchInputInactiveStyle = inactiveStyle;
  }

  public get pageSize(): number {
    if (this.dataIn.attri['pageSize'] != null) return this.dataIn.attri['pageSize'];
    else return 10;
  }

  public get pagingEnabled(): boolean {
    if (this.dataIn.attri['pagingEnabled'] != null) return this.dataIn.attri['pagingEnabled'];
    else return true;
  }

  public get context(): AccountTypes | 'account' {
    if (this.dataIn.attri['context'] != null) return this.dataIn.attri['context'];
    else return AccountTypes.CURRENT_ACCOUNT;
  }

  private dispatchContextBasedLoadingAction(status: boolean) {
    const contextBasedLoadingStateActions = {
      [AccountTypes.CURRENT_ACCOUNT]: new SetLoadingStatus(status),
      account: new SetLoadingStatus(status),
      [AccountTypes.SAVINGS_ACCOUNT]: new SetSavingLoadingStatus(status),
      [AccountTypes.OPEN_EASY_ACCESS_SAVINGS_ACCOUNT]: new SetSavingLoadingStatus(status),
      [AccountTypes.SAVINGS_INTERNAL_TRANSFER_ACCOUNT_FORM_CONTEXT]: new SetSavingLoadingStatus(status),
    };
    this.store.dispatch(contextBasedLoadingStateActions[this.context]);
  }

  private dispatchContextBasedFetchAction(additionalContext?: {
    accountContextStatusList: AccountStatusType[];
    searchTerm: { name: string } | undefined;
    viewMode: ViewType;
    pageSize: number;
    page?: number;
  }) {
    const payload: {
      pageNumber: number;
      pageSize: number;
      accountContextStatusList?: AccountStatusType[];
      searchTerm?: { name: string };
      viewMode?: ViewType;
    } = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      accountContextStatusList: additionalContext?.accountContextStatusList,
      searchTerm: additionalContext?.searchTerm,
      viewMode: additionalContext?.viewMode,
    };
    // reassign page size and number if they are available in the additionalContext
    payload.pageSize = additionalContext?.pageSize ?? this.pageSize;
    payload.pageNumber = additionalContext?.page ?? this.pageNumber;
    const currentAccountFetchAction = new GetAccount(
      payload.pageNumber,
      payload.pageSize,
      payload?.accountContextStatusList,
      payload?.searchTerm,
      payload?.viewMode ?? ViewType.LIST,
    );
    const savingsAccountFetchAction = new GetSavingAccount(
      payload.pageNumber,
      payload.pageSize,
      payload?.accountContextStatusList,
      payload?.searchTerm,
      payload?.viewMode ?? ViewType.LIST,
    );
    const contextBasedFetchActions = {
      [AccountTypes.CURRENT_ACCOUNT]: currentAccountFetchAction,
      account: currentAccountFetchAction,
      [AccountTypes.SAVINGS_ACCOUNT]: savingsAccountFetchAction,
      [AccountTypes.OPEN_EASY_ACCESS_SAVINGS_ACCOUNT]: savingsAccountFetchAction,
      [AccountTypes.SAVINGS_INTERNAL_TRANSFER_ACCOUNT_FORM_CONTEXT]: savingsAccountFetchAction,
    };
    this.store.dispatch(contextBasedFetchActions[this.context]);
  }

  public handleChildScroll(additionalContext?: {
    accountContextStatusList: AccountStatusType[];
    searchTerm: { name: string } | undefined;
    viewMode: ViewType;
    pageSize: number;
    page?: number;
  }) {
    if (additionalContext?.searchTerm) {
      this.dispatchContextBasedLoadingAction(true);
      this.dispatchContextBasedFetchAction(additionalContext);
      return;
    }
    if (this.pagingEnabled) {
      if (this.pageNumber != this.totalPages) {
        if (!additionalContext?.page) this.pageNumber = this.pageNumber + 1;
        this.dispatchContextBasedFetchAction(additionalContext);
      }
    }
  }
}
